import { getRepository } from '@things-factory/shell'
import { KpiMetric } from '../kpi-metric/kpi-metric'
import { Kpi } from './kpi'
import { KpiValue } from '../kpi-value/kpi-value'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'
import { validateFormulaSyntax } from '../../calculator/validate.js'

export interface FormulaValidationResult {
  /** 하드 에러(구문/미지 함수/순환참조)가 없으면 true. 경고는 valid 에 영향 없음. */
  valid: boolean
  errors: string[]
  warnings: string[]
}

/**
 * KPI formula 파싱/검증/매핑 유효성 검사 서비스
 * - formula에서 Metric 코드(변수명) 추출
 * - 각 Metric의 존재/매핑 유효성 검사
 * - 구문 오류, 미정의 Metric, 매핑 오류 등 에러 리포팅
 */
export class KpiFormulaService {
  /**
   * formula 문자열에서 Metric 코드(변수명) 추출
   * @param formula KPI formula (예: 'defect_count / total_count * 100')
   * @returns string[] 추출된 Metric 코드 리스트
   */
  extractMetricCodes(formula: string): string[] {
    // 변수명: 알파벳/언더스코어로 시작, 숫자/알파벳/언더스코어 포함
    const regex = /[a-zA-Z_][a-zA-Z0-9_]*/g
    // 숫자/예약어/연산자 제외(후처리 필요)
    const reserved = new Set(['if', 'else', 'return', 'true', 'false'])
    return Array.from(new Set((formula.match(regex) || []).filter(code => isNaN(Number(code)) && !reserved.has(code))))
  }

  /**
   * 순수(DB 무관) 구문 검증 — calculator/validate 로 위임 (단위 테스트는 그쪽에서).
   */
  validateFormulaSyntax(formula: string) {
    return validateFormulaSyntax(formula)
  }

  /**
   * formula 검증 + metric 참조/순환참조 검사.
   *
   * 정책:
   *  - **에러(저장 거부)**: 구문 오류, 미지 함수, 순환참조.
   *  - **경고(저장 허용)**: 미정의 metric, 데이터셋 필드 미매핑. (metric 시딩 순서 등 실무 유연성)
   *
   * @returns { valid, errors, warnings } — valid 는 errors 가 없을 때 true (warnings 무관).
   */
  async validateFormula(formula: string, visited: Set<string> = new Set()): Promise<FormulaValidationResult> {
    // 1) 순수 구문/함수 검증 — 하드 에러면 즉시 실패
    const { errors, warnings, variables } = this.validateFormulaSyntax(formula)
    if (errors.length) return { valid: false, errors, warnings }

    // 2) metric 참조 검증 (DB) — 미존재/미매핑은 경고, 순환참조는 에러
    const metricRepo = getRepository(KpiMetric)
    for (const code of variables) {
      if (visited.has(code)) {
        errors.push(`순환참조 감지: ${Array.from(visited).join(' → ')} → ${code}`)
        continue
      }
      visited.add(code)
      const metric = await metricRepo.findOne({ where: { name: code } })
      if (!metric) {
        warnings.push(`Metric '${code}' 미정의 (경고 — 저장은 허용)`)
        visited.delete(code)
        continue
      }
      if (!metric.dataSetId || !metric.fieldName) {
        warnings.push(`Metric '${code}' 데이터셋 필드 미매핑 (경고)`)
      }
      // metric 이 자체 formula 를 가지면 재귀 검증(순환참조 포함)
      if ((metric as any).formula) {
        const sub = await this.validateFormula((metric as any).formula, new Set(visited))
        errors.push(...sub.errors)
        warnings.push(...sub.warnings)
      }
      visited.delete(code)
    }
    return { valid: errors.length === 0, errors, warnings }
  }

  /**
   * 계층 구조 KPI의 값을 자식 KPI들로부터 집계 계산
   * @param kpiId 부모 KPI ID
   * @param valueDate 계산 대상 날짜
   * @param org 조직 단위
   * @returns Promise<number | null> 집계된 값
   */
  async calculateHierarchicalValue(kpiId: string, valueDate: string, org?: string): Promise<number | null> {
    const kpiRepo = getRepository(Kpi)
    const kpi = await kpiRepo.findOne({
      where: { id: kpiId },
      relations: ['children']
    })

    if (!kpi || !kpi.children || kpi.children.length === 0) {
      return null
    }

    const childValues: number[] = []
    const childWeights: number[] = []

    for (const child of kpi.children) {
      let childValue: number | null = null
      
      if (child.isLeaf) {
        // Leaf KPI인 경우 KpiValue에서 실제 값을 가져옴
        const kpiValueRepo = getRepository(KpiValue)
        let kpiValue: KpiValue | null = null
        if (org) {
          const kpiOrgScope = await getRepository(KpiOrgScope).findOne({
            where: { org: org, domain: { id: child.domain?.id } }
          })
          if (kpiOrgScope) {
            kpiValue = await kpiValueRepo.findOne({ 
              where: { 
                kpi: { id: child.id }, 
                valueDate,
                kpiOrgScope: { id: kpiOrgScope.id }
              }
            })
          }
        } else {
          // org가 없는 경우 kpiOrgScope 없이 조회 
          kpiValue = await kpiValueRepo.findOne({ 
            where: { 
              kpi: { id: child.id }, 
              valueDate,
              kpiOrgScope: null
            }
          })
        }
        childValue = kpiValue?.value || null
      } else {
        // 중간 노드인 경우 재귀적으로 계산
        childValue = await this.calculateHierarchicalValue(child.id, valueDate, org)
      }

      if (childValue !== null) {
        childValues.push(childValue)
        childWeights.push(child.weight || 1)
      }
    }

    if (childValues.length === 0) {
      return null
    }

    // 가중 평균 계산
    const totalWeight = childWeights.reduce((sum, weight) => sum + weight, 0)
    const weightedSum = childValues.reduce((sum, value, index) => sum + value * childWeights[index], 0)
    
    return totalWeight > 0 ? weightedSum / totalWeight : null
  }

  /**
   * KPI가 순환 참조를 생성하는지 확인
   * @param kpiId KPI ID
   * @param parentId 설정하려는 부모 KPI ID
   * @returns Promise<boolean> 순환 참조 여부
   */
  async hasCircularReference(kpiId: string, parentId: string): Promise<boolean> {
    if (kpiId === parentId) {
      return true
    }

    const kpiRepo = getRepository(Kpi)
    const parent = await kpiRepo.findOne({
      where: { id: parentId },
      relations: ['parent']
    })

    if (!parent || !parent.parent) {
      return false
    }

    return await this.hasCircularReference(kpiId, parent.parent.id)
  }
}
